/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import './subscription.css';

interface SubscriptionPlan {
  id: string;
  name: string;
  display_name: string;
  price: number;
  price_ngn: number;
  description: string;
  features: string[];
  courses_limit: number;
  theory_marked_limit: number;
  access_days: number;
  has_unlimited_mcq: boolean;
  has_heatmap: boolean;
  has_priority_marking: boolean;
  popular?: boolean;
}

interface UserProfile {
  id: string;
  credits: number;
  subscription_tier: string;
  referral_code: string;
  total_referrals: number;
  referral_credits_earned: number;
  email?: string;
  full_name?: string;
  phone?: string;
}

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  
  // User states
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Subscription states
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  
  // Referral states
  const [referralCode, setReferralCode] = useState('');
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralSuccess, setReferralSuccess] = useState('');

  useEffect(() => {
    loadUserData();
    loadSubscriptionPlans();
    // Load Paystack script
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('id, credits, subscription_tier, referral_code, total_referrals, referral_credits_earned, email, full_name, phone')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      setUserProfile(profile);
    } catch (err: any) {
      setError('Failed to load user data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionPlans = async () => {
    try {
      // New exam-focused plans based on your offers
      const examPlans: SubscriptionPlan[] = [
        {
          id: 'exam-week',
          name: 'exam_week',
          display_name: 'EXAM-WEEK PASS',
          price: 1.00, // USD equivalent for reference
          price_ngn: 1450,
          description: 'For less than a plate of rice, know exactly where you\'ll lose marks tomorrow.',
          features: [
            '1 course',
            '48 hours access',
            'Unlimited MCQs',
            '2 theory answers marked',
            'Full feedback',
            'Readiness score'
          ],
          courses_limit: 1,
          theory_marked_limit: 2,
          access_days: 2,
          has_unlimited_mcq: true,
          has_heatmap: false,
          has_priority_marking: false
        },
        {
          id: 'multi-course',
          name: 'multi_course',
          display_name: 'MULTI-COURSE PASS',
          price: 2.33, // USD equivalent for reference
          price_ngn: 3500,
          description: 'Use across any of your registered courses.',
          features: [
            'Up to 4 courses',
            '7 days access',
            'Unlimited MCQs',
            '8 theory answers marked',
            'Full feedback',
            'Heatmap + readiness score'
          ],
          courses_limit: 4,
          theory_marked_limit: 8,
          access_days: 7,
          has_unlimited_mcq: true,
          has_heatmap: true,
          has_priority_marking: false,
          popular: true
        },
        {
          id: 'semester',
          name: 'semester',
          display_name: 'SEMESTER PASS',
          price: 4.00, // USD equivalent for reference
          price_ngn: 6000,
          description: 'Complete semester preparation with priority support.',
          features: [
            'Up to 10 courses',
            '30 days access',
            'Unlimited MCQs',
            '25 theory answers marked',
            'Full analytics',
            'Priority marking (faster AI response)',
            'Deeper feedback insights',
            'Heatmap + readiness score'
          ],
          courses_limit: 10,
          theory_marked_limit: 25,
          access_days: 30,
          has_unlimited_mcq: true,
          has_heatmap: true,
          has_priority_marking: true
        }
      ];

      setPlans(examPlans);
      setSelectedPlan('multi-course'); // Select Multi-Course by default (most popular)
    } catch (err: any) {
      setError('Failed to load subscription plans: ' + err.message);
    }
  };

  const handlePurchase = async () => {
    if (!selectedPlan) {
      setError('Please select a plan');
      return;
    }

    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) return;

    setIsPurchasing(true);
    setError('');
    setSuccess('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('email, full_name, phone')
        .eq('id', user.id)
        .single();

      const userEmail = profile?.email || user.email || '';
      const userName = profile?.full_name || 'User';
      const userPhone = profile?.phone || '';

      // Generate transaction reference
      const transactionRef = `BBTUTOR-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

      // Create transaction record
      const { data: transaction, error: txError } = await supabase
        .from('transactions')
        .insert({
          user_id: user.id,
          plan_id: plan.id,
          amount: plan.price_ngn,
          currency: 'NGN',
          status: 'pending',
          transaction_reference: transactionRef,
          metadata: {
            plan_name: plan.display_name,
            courses_limit: plan.courses_limit,
            theory_marked_limit: plan.theory_marked_limit,
            access_days: plan.access_days,
            has_unlimited_mcq: plan.has_unlimited_mcq,
            user_email: userEmail,
            user_name: userName
          }
        })
        .select('id')
        .single();

      if (txError) throw txError;

      // Initialize Paystack payment
      const paystackOptions = {
        key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
        email: userEmail,
        amount: plan.price_ngn * 100, // Paystack uses kobo (multiply by 100)
        ref: transactionRef,
        metadata: {
          custom_fields: [
            {
              display_name: "Plan Name",
              variable_name: "plan_name",
              value: plan.display_name
            },
            {
              display_name: "Courses Limit",
              variable_name: "courses_limit",
              value: plan.courses_limit.toString()
            },
            {
              display_name: "Access Days",
              variable_name: "access_days",
              value: plan.access_days.toString()
            },
            {
              display_name: "Transaction ID",
              variable_name: "transaction_id",
              value: transaction.id.toString()
            }
          ]
        },
        callback: async (response: any) => {
          // Payment successful
          if (response.status === 'success') {
            // Verify payment on your backend
            const verifyResponse = await fetch('/api/subscription/verify-paystack', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                transactionRef: response.reference,
                paystackRef: response.transaction,
                transactionId: transaction.id
              })
            });

            const result = await verifyResponse.json();
            if (result.success) {
              setSuccess(`Payment successful! Your ${plan.display_name} has been activated.`);
              await loadUserData(); // Refresh user data
              // Redirect to dashboard after 2 seconds
              setTimeout(() => {
                navigate('/dashboard');
              }, 2000);
            } else {
              setError('Payment verification failed. Please contact support.');
            }
          } else {
            setError('Payment was not successful. Please try again.');
          }
          setIsPurchasing(false);
        },
        onClose: () => {
          setIsPurchasing(false);
          setError('Payment was cancelled. You can try again anytime.');
        }
      };

      // Open Paystack popup
      const paystack = new (window as any).PaystackPop();
      paystack.newTransaction(paystackOptions);

    } catch (err: any) {
      console.error('Purchase error:', err);
      setError('Payment initialization failed: ' + err.message);
      setIsPurchasing(false);
    }
  };

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) {
      setError('Please enter a referral code');
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const response = await fetch('/api/subscription/apply-referral', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          referralCode: referralCode.trim()
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setReferralSuccess('Referral code applied successfully! You will receive bonus benefits when the referred user makes a purchase.');
        setReferralCode('');
        setShowReferralInput(false);
        await loadUserData();
      } else {
        setError(result.message || 'Failed to apply referral code');
      }
    } catch (err: any) {
      setError('Failed to apply referral code: ' + err.message);
    }
  };

  const copyReferralCode = () => {
    if (userProfile?.referral_code) {
      navigator.clipboard.writeText(userProfile.referral_code);
      setSuccess('Referral code copied to clipboard!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const formatPriceNGN = (price: number) => {
    return `₦${price.toLocaleString()}`;
  };

  const getSelectedPlanDetails = () => {
    if (!selectedPlan) return null;
    return plans.find(p => p.id === selectedPlan);
  };

  const selectedPlanDetails = getSelectedPlanDetails();

  if (loading) {
    return (
      <div className="subscription-container">
        <div className="loading-wrapper">
          <div className="spinner-large"></div>
          <p className="loading-text">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-container">
      {/* Header */}
      <div className="subscription-header">
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1 className="subscription-title">🎓 Exam Prep Passes</h1>
        <p className="subscription-subtitle">
          Choose the pass that matches your exam preparation needs. All prices in Nigerian Naira (₦).
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="message-alert error">
          <span className="alert-icon">⚠️</span>
          <span className="alert-text">{error}</span>
        </div>
      )}
      {success && (
        <div className="message-alert success">
          <span className="alert-icon">✅</span>
          <span className="alert-text">{success}</span>
        </div>
      )}
      {referralSuccess && (
        <div className="message-alert info">
          <span className="alert-icon">💡</span>
          <span className="alert-text">{referralSuccess}</span>
        </div>
      )}

      {/* Current Status Card - Updated for Pass-based system */}
      <div className="current-status-card">
        <div className="status-header">
          <h2>Your Current Status</h2>
        </div>
        <div className="status-content">
          <div className="status-item">
            <div className="status-label">Active Pass</div>
            <div className="status-value">
              {userProfile?.subscription_tier === 'exam_week' ? 'EXAM-WEEK PASS' :
               userProfile?.subscription_tier === 'multi_course' ? 'MULTI-COURSE PASS' :
               userProfile?.subscription_tier === 'semester' ? 'SEMESTER PASS' :
               'No Active Pass'}
            </div>
          </div>
          <div className="status-item">
            <div className="status-label">Referrals</div>
            <div className="status-value">
              {userProfile?.total_referrals || 0} referrals • {userProfile?.referral_credits_earned || 0} bonus credits
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Plans Grid - Updated with new offers */}
      <div className="plans-section">
        <h2 className="section-title">Choose Your Pass</h2>
        <p className="section-subtitle">
          Each pass gives you access to exam simulations, AI marking, and detailed feedback.
        </p>

        <div className="plans-grid">
          {plans.map(plan => (
            <div
              key={plan.id}
              className={`plan-card ${selectedPlan === plan.id ? 'selected' : ''} ${plan.popular ? 'popular' : ''}`}
              onClick={() => setSelectedPlan(plan.id)}
            >
              {plan.popular && (
                <div className="popular-badge">⭐ MOST POPULAR</div>
              )}
              
              <div className="plan-header">
                <h3 className="plan-name">{plan.display_name}</h3>
                <div className="plan-price">
                  {formatPriceNGN(plan.price_ngn)}
                </div>
                <div className="plan-access">{plan.access_days} days access</div>
              </div>

              <div className="plan-description">
                {plan.description}
              </div>

              <div className="plan-features">
                <div className="feature-item highlight">
                  <span className="feature-icon">📚</span>
                  <span className="feature-text">Up to {plan.courses_limit} course{plan.courses_limit > 1 ? 's' : ''}</span>
                </div>
                {plan.has_unlimited_mcq && (
                  <div className="feature-item">
                    <span className="feature-icon">∞</span>
                    <span className="feature-text">Unlimited MCQs</span>
                  </div>
                )}
                <div className="feature-item">
                  <span className="feature-icon">✍️</span>
                  <span className="feature-text">{plan.theory_marked_limit} theory answers marked</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📊</span>
                  <span className="feature-text">Full feedback + Readiness score</span>
                </div>
                {plan.has_heatmap && (
                  <div className="feature-item">
                    <span className="feature-icon">🔥</span>
                    <span className="feature-text">Performance heatmap</span>
                  </div>
                )}
                {plan.has_priority_marking && (
                  <div className="feature-item">
                    <span className="feature-icon">⚡</span>
                    <span className="feature-text">Priority marking (faster AI response)</span>
                  </div>
                )}
                {plan.id === 'semester' && (
                  <div className="feature-item">
                    <span className="feature-icon">🔍</span>
                    <span className="feature-text">Deeper feedback insights</span>
                  </div>
                )}
              </div>

              <div className="plan-action">
                <button
                  className={`select-plan-btn ${selectedPlan === plan.id ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPlan(plan.id);
                  }}
                >
                  {selectedPlan === plan.id ? '✓ Selected' : 'Select Pass'}
                </button>
              </div>

              {/* Value Highlight */}
              {plan.id === 'exam-week' && (
                <div className="value-indicator special">
                  <span className="value-text">🍚 Less than a plate of rice!</span>
                </div>
              )}
              {plan.id === 'multi-course' && (
                <div className="value-indicator">
                  <span className="value-text">Best value for multiple courses</span>
                </div>
              )}
              {plan.id === 'semester' && (
                <div className="value-indicator">
                  <span className="value-text">Complete semester preparation</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Purchase Button */}
        {selectedPlanDetails && (
          <div className="purchase-section">
            <div className="selected-plan-summary">
              <h3>Selected: {selectedPlanDetails.display_name}</h3>
              <p>
                Get <strong>{selectedPlanDetails.access_days} days access</strong> to{' '}
                <strong>{selectedPlanDetails.courses_limit} course{selectedPlanDetails.courses_limit > 1 ? 's' : ''}</strong> with{' '}
                <strong>{selectedPlanDetails.theory_marked_limit} marked theory answers</strong>
              </p>
              <p className="total-price">
                Total: <strong>{formatPriceNGN(selectedPlanDetails.price_ngn)}</strong>
              </p>
            </div>
            <button
              className="purchase-btn"
              onClick={handlePurchase}
              disabled={isPurchasing}
            >
              {isPurchasing ? (
                <>
                  <span className="loading-spinner"></span>
                  Processing Payment...
                </>
              ) : (
                `Pay ${formatPriceNGN(selectedPlanDetails.price_ngn)} with Paystack`
              )}
            </button>
            <p className="payment-note">
              🔒 Secure payment powered by Paystack. All major cards and local payment methods accepted (Card, Bank Transfer, USSD, QR Code).
            </p>
          </div>
        )}
      </div>

      {/* What's Included - Detailed Breakdown */}
      <div className="included-section">
        <h2 className="section-title">What's Included in Every Pass</h2>
        <div className="included-grid">
          <div className="included-card">
            <div className="included-icon">🎯</div>
            <h3>Real Exam Simulation</h3>
            <p>Experience actual exam conditions with timed tests and pressure simulation</p>
          </div>
          <div className="included-card">
            <div className="included-icon">🤖</div>
            <h3>AI Lecturer Marking</h3>
            <p>Get your answers marked like a real lecturer would, with detailed feedback</p>
          </div>
          <div className="included-card">
            <div className="included-icon">📈</div>
            <h3>Performance Analytics</h3>
            <p>Track your progress with heatmaps, readiness scores, and weak spot identification</p>
          </div>
          <div className="included-card">
            <div className="included-icon">💡</div>
            <h3>Smart Study Prioritization</h3>
            <p>AI identifies what to study based on exam patterns and your weak areas</p>
          </div>
        </div>
      </div>

      {/* Referral Program */}
      <div className="referral-section">
        <div className="referral-card">
          <div className="referral-header">
            <h2>🎁 Referral Program</h2>
            <div className="referral-badge">Earn bonuses</div>
          </div>
          
          <div className="referral-content">
            <div className="referral-info">
              <h3>Invite friends, both benefit!</h3>
              <p>
                Share your referral code with friends. When they purchase any pass,
                you'll receive <strong>bonus features</strong> and they'll get <strong>extra benefits</strong> too!
              </p>
              
              <div className="referral-stats">
                <div className="referral-stat">
                  <div className="stat-value">{userProfile?.total_referrals || 0}</div>
                  <div className="stat-label">Friends Referred</div>
                </div>
                <div className="referral-stat">
                  <div className="stat-value">{userProfile?.referral_credits_earned || 0}</div>
                  <div className="stat-label">Bonuses Earned</div>
                </div>
              </div>
            </div>

            <div className="referral-actions">
              <div className="your-referral-code">
                <label>Your Referral Code:</label>
                <div className="code-display">
                  <code className="referral-code">{userProfile?.referral_code || 'GENERATING...'}</code>
                  <button className="copy-code-btn" onClick={copyReferralCode}>
                    📋 Copy
                  </button>
                </div>
                <p className="code-hint">
                  Share this code with friends when they sign up
                </p>
              </div>

              <div className="apply-referral">
                {!showReferralInput ? (
                  <button
                    className="apply-referral-btn"
                    onClick={() => setShowReferralInput(true)}
                  >
                    🔗 Have a referral code?
                  </button>
                ) : (
                  <div className="referral-input-group">
                    <input
                      type="text"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value)}
                      placeholder="Enter referral code"
                      className="referral-input"
                    />
                    <button className="apply-btn" onClick={handleApplyReferral}>
                      Apply
                    </button>
                    <button
                      className="cancel-btn"
                      onClick={() => {
                        setShowReferralInput(false);
                        setReferralCode('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="referral-steps">
            <h4>How it works:</h4>
            <div className="steps-grid">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h5>Share Your Code</h5>
                  <p>Give your referral code to friends</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h5>They Sign Up</h5>
                  <p>They enter your code during signup</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h5>They Purchase</h5>
                  <p>They buy any exam pass</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h5>You Both Win</h5>
                  <p>You get bonuses, they get extra benefits</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section - Updated for Pass-based system */}
      <div className="faq-section">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h3>What's the difference between the passes?</h3>
            <p>EXAM-WEEK PASS (₦1,450) is for 1 course, 48 hours. MULTI-COURSE PASS (₦3,500) is for up to 4 courses, 7 days. SEMESTER PASS (₦6,000) is for up to 10 courses, 30 days with priority marking.</p>
          </div>
          <div className="faq-item">
            <h3>How does the AI marking work?</h3>
            <p>Our AI analyzes your answers against lecturer marking rubrics, showing you exactly where you gain or lose marks, and why.</p>
          </div>
          <div className="faq-item">
            <h3>Can I upgrade my pass?</h3>
            <p>Yes! You can upgrade anytime. The cost of your remaining time will be credited toward the new pass.</p>
          </div>
          <div className="faq-item">
            <h3>What payment methods do you accept?</h3>
            <p>We accept all major cards (Visa, Mastercard, Verve), Bank Transfers, USSD, and QR Code payments via Paystack.</p>
          </div>
          <div className="faq-item">
            <h3>When does my pass start?</h3>
            <p>Your pass activates immediately after successful payment confirmation.</p>
          </div>
          <div className="faq-item">
            <h3>Can I get a refund?</h3>
            <p>We offer refunds within 24 hours of purchase if you haven't used any of your marked theory answers.</p>
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div className="support-section">
        <div className="support-card">
          <div className="support-icon">💬</div>
          <div className="support-content">
            <h3>Need help choosing a pass?</h3>
            <p>Our support team is here to help you find the right pass for your exam preparation needs.</p>
            <button className="support-btn" onClick={() => navigate('/support')}>
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;